import { useState, useCallback, useRef } from "react";

export interface AsyncHandlerResult {
  loading: boolean;
  error: string | null;
  handle: () => Promise<void>;
}

/**
 * Wraps an async function with loading/error lifecycle for on-demand calls.
 * Counterpart to useAsyncData for mutations and user-triggered actions.
 * fn is stored in a ref so callers do not need to memoize it.
 */
export function useAsyncHandler(fn: () => Promise<void>): AsyncHandlerResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fnRef = useRef(fn);
  fnRef.current = fn;

  const handle = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fnRef.current();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, handle };
}
